import React from "react";
import styled from "styled-components";
import {
  LuBot,
  LuBrain,
  LuChartColumn,
  LuDatabase,
  LuNetwork,
  LuScanEye,
} from "react-icons/lu";
import {
  Container,
  IconBox,
  Reveal,
  Section,
  SectionHeader,
  Tag,
  TagList,
} from "./ui";

const services = [
  {
    icon: <LuBrain />,
    title: "Artificial Intelligence",
    description:
      "Custom AI solutions covering machine learning, natural language processing and computer vision to automate and optimize your business processes.",
    techStack: ["TensorFlow", "PyTorch", "OpenAI", "Scikit-learn"],
  },
  {
    icon: <LuChartColumn />,
    title: "Data Analytics",
    description:
      "Turn raw data into actionable insight with advanced statistical analysis, interactive dashboards and predictive models.",
    techStack: ["Python", "R", "Pandas", "Power BI"],
  },
  {
    icon: <LuBot />,
    title: "Intelligent Automation",
    description:
      "Bots and automation systems that streamline your workflows, remove repetitive tasks and improve operational efficiency.",
    techStack: ["RPA", "Chatbots", "AutoML", "APIs"],
  },
  {
    icon: <LuDatabase />,
    title: "Big Data & ETL",
    description:
      "Scalable data architectures, robust ETL pipelines and real-time processing built to handle large volumes of data.",
    techStack: ["Apache Spark", "Kafka", "Airflow", "SQL"],
  },
  {
    icon: <LuScanEye />,
    title: "Computer Vision",
    description:
      "Image recognition, object detection, video analytics and image processing for industrial and commercial applications.",
    techStack: ["OpenCV", "YOLO", "CNN", "Computer Vision"],
  },
  {
    icon: <LuNetwork />,
    title: "MLOps & Deployment",
    description:
      "Production model deployment with continuous monitoring, model versioning, dedicated CI/CD pipelines and optimized cloud infrastructure.",
    techStack: ["MLflow", "Kubeflow", "Docker", "Kubernetes"],
  },
];

const Grid = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Card = styled(Reveal)`
  display: flex;
  flex-direction: column;
  padding: 32px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: var(--color-border-strong);
    box-shadow: var(--shadow-md);
  }

  h3 {
    font-size: 1.25rem;
    margin: 24px 0 10px;
  }

  p {
    flex: 1;
    font-size: 0.9375rem;
    color: var(--color-muted);
    margin-bottom: 24px;
  }

  @media (max-width: 640px) {
    padding: 24px;
  }
`;

const AIDataServices = () => (
  <Section id="ai-data">
    <Container>
      <SectionHeader
        eyebrow="AI & Data"
        title="AI solutions & data analytics"
        lead="Put artificial intelligence and data analytics to work: transform how your business operates and make decisions grounded in your own data."
      />

      <Grid>
        {services.map((service, index) => (
          <Card key={service.title} forwardedAs="li" delay={(index % 3) * 0.05}>
            <IconBox aria-hidden="true">{service.icon}</IconBox>
            <h3>{service.title}</h3>
            <p>{service.description}</p>
            <TagList aria-label={`${service.title} technologies`}>
              {service.techStack.map(tech => (
                <Tag key={tech}>{tech}</Tag>
              ))}
            </TagList>
          </Card>
        ))}
      </Grid>
    </Container>
  </Section>
);

export default AIDataServices;
